import axios from 'axios';
import { graphql, useStaticQuery } from 'gatsby';
import React, {
  FormEvent, useContext, useEffect, useState,
} from 'react';
import ShowSmsFormStepOne from './SmsForm/ShowSmsFormStepOne';
import ShowSmsFormStepTwo from './SmsForm/ShowSmsFormStepTwo';
import ShowSmsFormSubmit from './SmsForm/ShowSmsFormSubmit';
import { GlobalContext } from '../GlobalContext';

const validatePhoneNum = (phoneNumber: string) => {
  const regex = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;
  return regex.test(phoneNumber);
};

type UserData = {
  phone: string
  firstName: string
  lastName: string
}

const createOrUpdateUserByPhone = async (user: UserData, shows: Array<string>) => {
  try {
    const { data } = await axios.post('/api/sms', {
      contactPhone: user.phone,
      firstName: user?.firstName,
      lastName: user?.lastName,
      shows,
    });
    return data;
  } catch (err:any) {
    return { success: false, data: err };
  }
};

type Props = {
  modal?: boolean,
}

function ShowSmsReminders({ modal }: Props) {
  const globalContext = useContext(GlobalContext);
  const [currentShowList, setCurrentShowList] = useState<string[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [formIsValid, setFormIsValid] = useState<boolean>(false);
  const [formStep, setFormStep] = useState<number>(0);
  const [isValidPhoneNum, setIsValidPhoneNum] = useState<boolean>(false);
  const [loadingStep, setLoadingStep] = useState<boolean>(false);
  const [savedShows, setSavedShows] = useState<string[]>([]);
  const [tosUserAgreement, setTosUserAgreement] = useState(false);
  const [userInfo, setUserInfo] = useState<UserData>({
    firstName: '',
    lastName: '',
    phone: '',
  });

  const numbersEligibleUSCanada = 'Currently only eligible for numbers in the U.S. and Canada.';
  const requiredNumberWarning = 'REQUIRED. Phone number must be in a standard 10-digit format: 1234567890 or 123-456-7890';

  const query = useStaticQuery(graphql`
    {
      allContentfulShow(
        filter: {node_locale: {eq: "en"}, visible: {eq: true}, showInGetAlerts: {eq: true}}
      ) {
        nodes {
          title
        }
      }
      allContentfulEvent(
        filter: {node_locale: {eq: "en"}, visible: {eq: true}, showInGetAlerts: {eq: true}}
      ) {
        nodes {
          title
        }
      }
    }
  `);

  const activeShows = [
    ...query.allContentfulShow?.nodes?.map((show: any) => show.title) || [],
    ...query.allContentfulEvent?.nodes?.map((event: any) => event.title) || [],
  ];

  const additionalAlertOptions : string[] = (process.env.GATSBY_SIMPLE_TEXTING_CHANNELS || '').split(',') || [];

  if (additionalAlertOptions?.length > 0) {
    additionalAlertOptions.forEach((o) => {
      if (o && !activeShows?.includes((o))) activeShows?.unshift(o);
    });
  }

  activeShows
    .sort((a: string, b: string) => a.localeCompare(b, undefined, { ignorePunctuation: true }));

  const handleCloseModal = () => {
    if (globalContext) {
      const { showModal, setShowModal } = globalContext;
      setShowModal(!showModal);
    }
  };
  const resetForm = () => {
    setErrorMessage(null);
    setFormStep(0);
    setLoadingStep(false);
    setTosUserAgreement(false);
  };

  React.useEffect(
    () => () => resetForm(),
    [],
  );

  const handleCheckbox = (target: string, checked: boolean) => {
    if (!checked) {
      setSavedShows((savedShows) => savedShows.filter((name: string) => target !== name));
      return;
    }
    setSavedShows((savedShows) => [...savedShows, target]);
  };

  const handleFormSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!loadingStep) setLoadingStep(true);
    try {
      if (formStep === 0) {
        if (errorMessage) setErrorMessage(null);
        try {
          const { data: response } = await axios.get('/api/sms', {
            params: {
              userId: userInfo.phone,
            },
          });
          if (response?.success) {
            setUserInfo({
              ...userInfo,
              firstName: response?.data.firstName,
              lastName: response?.data.lastName,
            });
            response.data?.lists.map((listName: any) => handleCheckbox(listName.name, true));
            setFormStep(formStep + 1);
            setLoadingStep(false);
          } else if (response?.data?.code) {
            // moving on to next step, received lookup code error from api
            // meaning user wasn't found
            setFormStep(formStep + 1);
            setLoadingStep(false);
          } else {
            setErrorMessage('Error while looking up by phone number');
            setLoadingStep(false);
          }
        } catch (err:any) {
          setErrorMessage('Error while looking up by phone number');
          setLoadingStep(false);
        }
      } else if (formStep === 1) {
        if (errorMessage) {
          resetForm();
        } else {
          const { success, data } = await createOrUpdateUserByPhone(
            userInfo,
            savedShows,
          );
          if (success) {
            setFormStep(formStep + 1);
            setLoadingStep(false);
          } else {
            setErrorMessage(`Error: ${data?.code}. More details: ${data?.response?.data?.data?.code}`);
            setLoadingStep(false);
          }
        }
      } else if (formStep > 1) {
        if (modal) {
          handleCloseModal();
        } else {
          resetForm();
        }
      }
    } catch (err:any) {
      setErrorMessage(`Error: ${err?.response?.data?.code}. More details: ${err?.response?.data?.data?.code}`);
      setLoadingStep(false);
    }
  };
  // run every state change
  useEffect(() => {
    const validPhoneNum = validatePhoneNum(userInfo.phone);
    if (validPhoneNum && !isValidPhoneNum) {
      setIsValidPhoneNum(true);
    }
    if (!validPhoneNum && isValidPhoneNum) setIsValidPhoneNum(false);
    const checkIfFormIsValid = () => {
      if (formStep === 0) {
        // eslint-disable-next-line no-unused-expressions
        isValidPhoneNum ? setFormIsValid(true) : setFormIsValid(false);
      } else if (formStep > 0) {
        if (
          isValidPhoneNum
              && userInfo.firstName
              && tosUserAgreement
        ) {
          setFormIsValid(true);
        } else setFormIsValid(false);
      }
    };
    checkIfFormIsValid();
  });
  //
  const updateAlertListOptions = (
    activeShowList: string[] | undefined,
    savedShowList: string[] | undefined,
  ) => {
    const newActiveShowList: string[] | undefined = activeShowList;
    if (newActiveShowList) {
      setCurrentShowList(newActiveShowList?.filter((s: string) => savedShowList?.includes(s)));
    }
  };
  // run once on first render
  useEffect(() => {
    updateAlertListOptions(activeShows, savedShows);
  }, []);
  // set active show list since some values returned from simpletexting
  // may not be a show but still valid so ignore on front end but can't
  // overwrite on backend in simpletexting when going to update info
  useEffect(() => {
    updateAlertListOptions(activeShows, savedShows);
  }, [savedShows]);

  return (
    <div className={`${!modal ? 'mx-auto mt-[20px] lg:mt-[40px]' : ''} max-w-[685px] p-5`}>
      <div className={!modal ? '' : ''}>
        <form onSubmit={handleFormSubmit} className={`${formStep > 0 ? 'bg-[#FBFAF7]' : 'bg-chdBlue-light'} flex flex-col items-center w-full px-6 py-10  text-darkGreyBlue text-[16px]`}>
          <h3 className="font-serif text-2xl font-bold text-center">
            Sign up for Show Text Reminders
            <span className="italic"> for free!</span>
          </h3>
          {formStep === 0 && (
            <ShowSmsFormStepOne
              isValidPhoneNum={isValidPhoneNum}
              formIsValid={formIsValid}
              loadingStep={loadingStep}
              numbersEligibleUSCanada={numbersEligibleUSCanada}
              requiredNumberWarning={requiredNumberWarning}
              setUserInfo={setUserInfo}
              userInfo={userInfo}
              errorMessage={errorMessage}
            />
          )}
          {formStep > 0 && (
            <ShowSmsFormStepTwo
              currentShowList={currentShowList}
              isValidPhoneNum={isValidPhoneNum}
              formStep={formStep}
              fullShowList={activeShows}
              savedShows={savedShows}
              handleCheckbox={handleCheckbox}
              numbersEligibleUSCanada={numbersEligibleUSCanada}
              setUserInfo={setUserInfo}
              userInfo={userInfo}
              tosUserAgreement={tosUserAgreement}
              setTosUserAgreement={setTosUserAgreement}
            />
          )}
          <ShowSmsFormSubmit
            currentShowList={currentShowList}
            formIsValid={formIsValid}
            formStep={formStep}
            loadingStep={loadingStep}
            errorMessage={errorMessage}
          />
        </form>
      </div>
    </div>
  );
}

ShowSmsReminders.defaultProps = {
  modal: true,
};

export default ShowSmsReminders;
